<template>
  <div>
    <h3 class="pt-3">{{ title }}</h3>
    <p>{{ introductionText }}</p>
    <b-card no-body class="mt-3">
      <b-tabs ref="tabs" v-model="currentPage" card>
        <b-tab
          ref="info"
          :title="$t('complaint.information')"
          :disabled="complaintCreated || uploading"
        >
          <p>{{ instructionText }}</p>
          <hr />
          <b-form no-validate>
            <b-row>
              <b-col cols="12">
                <b-form-group>
                  {{ $t("complaint.fields.anonymous") }}
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="
                      complaint.anonymous ? 'danger' : 'secondary'
                    "
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto ml-1"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="complaint.anonymous ? 'secondary' : 'info'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.fullName">
                  <b-form-input
                    v-model="complaint.denouncer.fullName"
                    v-validate="rules.denouncer.fullName"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidFullName"
                    name="fullName"
                    :state="validateState('fullName')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidFullName">
                    {{ errors.first("fullName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col v-if="!hasCustomFields" cols="12" lg="3">
                <b-form-group :label="i18n.position">
                  <b-form-input
                    v-model="complaint.denouncer.position"
                    v-validate="rules.denouncer.position"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPosition"
                    name="position"
                    :state="validateState('position')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPosition">
                    {{ errors.first("position") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.phone">
                  <b-form-input
                    ref="phone"
                    v-model="complaint.denouncer.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.denouncer.phone"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :state="validateState('phone')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.email">
                  <b-form-input
                    v-model="complaint.denouncer.email"
                    v-validate="rules.denouncer.email"
                    type="email"
                    class="form-control"
                    aria-describedby="invalidEmail"
                    name="email"
                    :state="validateState('email')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidEmail">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="6">
                <b-form-group>
                  {{ $t("complaint.fields.previousProtocol.desc") }}
                  <b-form-checkbox
                    v-model="hasPreviousProtocol"
                    class="mb-auto"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="
                      hasPreviousProtocol ? 'secondary' : 'danger'
                    "
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="hasPreviousProtocol"
                    class="mb-auto ml-1"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="hasPreviousProtocol ? 'info' : 'secondary'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="3">
                <b-form-group
                  v-if="hasPreviousProtocol"
                  :label="i18n.previousProtocol"
                >
                  <b-form-input
                    v-model="complaint.previousProtocol"
                    v-validate="rules.previousProtocol"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPreviousProtocol"
                    name="previousProtocol"
                    :state="validateState('previousProtocol')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPreviousProtocol">
                    {{ errors.first("previousProtocol") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="hasCustomFields && reportingAgentEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group
                  label="Informe sua relação com a Caixa Vida e Previdência"
                >
                  <b-form-select
                    v-model="complaint.classification.reportingAgent"
                    v-validate="{ required: true }"
                    :options="classificationOptions.reportingAgent"
                    aria-describedby="invalid-reportingAgent"
                    name="reportingAgent"
                    :state="validateState('reportingAgent')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-reportingAgent">
                    {{ errors.first("reportingAgent") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Informe o local do ocorrido"
                  :description="$t('complaint.fields.location.desc')"
                >
                  <b-form-textarea
                    v-model="complaint.location"
                    v-validate="rules.location"
                    class="form-control"
                    aria-describedby="invalidLocation"
                    name="complaintLocation"
                    :rows="2"
                    :state="validateState('complaintLocation')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidLocation">
                    {{ errors.first("complaintLocation") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="hasCustomFields">
              <b-col cols="12" lg="4" md="6">
                <b-form-group
                  label="Motivo da denúncia"
                  :description="$t('complaint.fields.typology.description')"
                >
                  <b-form-select
                    v-model="complaint.typology"
                    v-validate="rules.typology"
                    :options="typologyOptions"
                    class="form-control"
                    aria-describedby="invalidTypology"
                    name="typology"
                    :state="validateState('typology')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidTypology">
                    {{ errors.first("typology") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Escreva a denúncia usando o máximo de detalhes que possuir "
                  :description="$t('complaint.fields.text.desc')"
                >
                  <b-form-textarea
                    v-model="complaint.text"
                    v-validate="rules.text"
                    class="form-control"
                    aria-describedby="invalidText"
                    name="complaintText"
                    :rows="4"
                    :state="validateState('complaintText')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidText">
                    {{ errors.first("complaintText") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Informe a data do ocorrido, mesmo que aproximada"
                  :description="$t('complaint.fields.date.desc')"
                >
                  <b-form-textarea
                    v-model="complaint.date"
                    v-validate="rules.date"
                    class="form-control"
                    aria-describedby="invalidDate"
                    name="complaintDate"
                    :rows="2"
                    :state="validateState('complaintDate')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidDate">
                    {{ errors.first("complaintDate") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Informe o nome das pessoas que testemunharam o fato ou que tenham conhecimento dele"
                >
                  <b-form-textarea
                    v-model="complaint.witnesses"
                    v-validate="rules.witnesses"
                    class="form-control"
                    aria-describedby="invalidWitnesses"
                    name="complaintWitnesses"
                    :rows="2"
                    :state="validateState('complaintWitnesses')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidWitnesses">
                    {{ errors.first("complaintWitnesses") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab
          :title="$t('complaint.files')"
          :disabled="(!infoValidated || complaintCreated) && !uploading"
        >
          <h5 class="pt-1 pb-1">{{ $t("complaint.filesInstruction") }}</h5>
          <multiple-file-upload
            ref="upload"
            :post-action="upload.postAction"
            :data="upload.data"
            :disabled="uploading || uploadComplete"
            @upload-complete="onUploadComplete"
          ></multiple-file-upload>
        </b-tab>
        <b-tab
          :title="$t('complaint.confirmation')"
          :disabled="!infoValidated || uploading"
        >
          <template v-if="complaintCreated">
            <h3>{{ $t("complaint.success") }}</h3>
            <h5>{{ $t("complaint.successInfo") }}</h5>
            <h5>
              {{ $t("labels.protocol") }}:
              <strong>{{ context.protocol }}</strong>
            </h5>
          </template>

          <template v-else>
            <b-form-group>
              <b-form-checkbox
                v-model="complaint.termsAccepted"
                v-validate="rules.termsAccepted"
                class="mb-auto"
                style="white-space: pre-line"
                :value="true"
                :unchecked-value="false"
                name="termsAccepted"
                :state="validateState('termsAccepted')"
                >Concordo que a Shield Compliance salve e envie as informações e
                arquivos disponibilizados por mim, à Caixa Vida e Previdência,
                possibilitando que as medidas sejam tomadas a partir da análise
                do conteúdo de minhas respostas. Para mais informações sobre
                proteção de dados e direitos do usuário, acesse a
                <a
                  href="https://portalprivacidade.caixavidaeprevidencia.com.br/s/"
                  >nossa política de privacidade</a
                >.
              </b-form-checkbox>
            </b-form-group>
          </template>
        </b-tab>
      </b-tabs>
      <template slot="footer">
        <template v-if="complaintCreated">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="$router.replace('/')"
            ><i class="fas fa-home" aria-hidden="true"></i> Início
          </b-button>
        </template>
        <template v-else>
          <b-button class="mr-1" :disabled="loading" @click="previousPage">{{
            $t("complaint.back")
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="
              loading || (currentPage === 1 && $refs.upload.hasInvalidFiles)
            "
            @click="nextPage"
          >
            {{
              currentPage === 2 ? $t("complaint.finish") : $t("complaint.next")
            }}
          </b-button>
        </template>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ValidatorDictionary } from "@/i18n";
import { mask } from "vue-the-mask";
import MultipleFileUpload from "../../../components/MultipleFileUpload";
import { sendComplaint } from "@/api/main";
import typologyValues from "../../../../../staticData/typology.json";

export default {
  name: "CompanyComplaint",
  directives: { mask },
  components: { MultipleFileUpload },
  data() {
    return {
      hasPreviousProtocol: false,
      complaint: {
        termsAccepted: false,
        dataConsentAccepted: false,
        anonymous: true,
        denouncer: {},
        classification: {},
      },
      currentPage: 0,
      totalPages: 3,
      infoValidated: false,
      loading: false,
      uploading: false,
      uploadComplete: false,
      upload: {
        postAction: "",
        data: {},
      },
      context: {
        protocol: null,
      },
      complaintCreated: false,
    };
  },
  computed: {
    ...mapGetters([
      "company",
      "portal",
      "classificationOptions",
      "enabledClassificationOptions",
    ]),
    title() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.title
      )
        return portal.languages[lang].complaint.title;
      return this.$i18n.t("complaint.title");
    },
    introductionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.introductionText
      )
        return portal.languages[lang].complaint.introductionText;
      return this.$i18n.t("complaint.introduction", [this.company.fantasyName]);
    },
    instructionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.instructionText
      )
        return portal.languages[lang].complaint.instructionText;
      return this.$i18n.t("complaint.instruction");
    },
    rules() {
      return {
        denouncer: {
          fullName: { required: !this.complaint.anonymous },
          position: { required: false },
          email: {
            required: this.hasCustomFields && !this.complaint.anonymous,
            email: true,
          },
          phone: {
            required: this.hasCustomFields && !this.complaint.anonymous,
          },
        },
        previousProtocol: { required: this.hasPreviousProtocol },
        text: { required: true },
        date: { required: false },
        location: { required: false },
        knowAbout: { required: false },
        witnesses: { required: false },
        financialInfo: { required: false },
        termsAccepted: { required: true, acceptance: true },
        dataConsentAccepted: {
          required: !this.complaint.anonymous,
          acceptance: true,
        },
        typology: { required: this.hasCustomFields },
      };
    },

    reportingAgentEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "reportingAgent"
      );
    },

    complaintClassificationOptions() {
      return this.enabledClassificationOptions.filter((opt) => {
        if (this.hasCustomFields && opt.name === "reportingAgent") {
          return false;
        }
        return (
          !opt.name.startsWith("ombudsman") && !opt.name.startsWith("dsar")
        );
      });
    },

    hasCustomFields() {
      return this.portal?.slug === "caixavidaeprevidencia";
    },

    i18n() {
      return ValidatorDictionary[this.$i18n.locale].attributes;
    },
    typologyOptions() {
      return Object.entries(typologyValues).reduce((data, [value, text]) => {
        data.push({ value, text });
        return data;
      }, []);
    },
  },

  created() {
    const updateEls = ["phone"];
    this.$nextTick(() => {
      for (const id of updateEls) {
        this.$refs[id]?.$el.oninput({ isTrusted: true });
      }
      this.$validator.reset();
    });
  },

  methods: {
    sanitizePayload(payload) {
      if (payload.denouncer.phone) {
        payload.denouncer.phone = payload.denouncer.phone.replace(/[^\d]/g, "");
        if (!payload.denouncer.phone) delete payload.denouncer.phone;
      }
      if (payload.denouncer.cpf) {
        payload.denouncer.cpf = payload.denouncer.cpf.replace(/[^\d]/g, "");
        if (!payload.denouncer.cpf) delete payload.denouncer.cpf;
      }
    },
    async submitComplaint() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.complaint));
        this.sanitizePayload(payload);

        const response = await sendComplaint(this.portal.slug, payload);
        if (response.status === 201) {
          const { protocol } = response.data;
          const files = this.$refs.upload.selectedFiles();
          this.context = {
            protocol,
          };
          if (files.length > 0) {
            this.uploading = true;
            this.currentPage = 1;

            this.upload.postAction = `/api/complaints/${this.portal.slug}/${protocol}/files`;
            for (const file of files) {
              file.postAction = this.upload.postAction;
              file.headers = this.upload.headers;
            }

            this.$nextTick(() => this.$refs.upload.start());
          } else {
            this.onComplaintCreated(protocol);
          }
        }
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onUploadComplete() {
      this.uploadComplete = true;
      this.uploading = false;
      this.$nextTick(() => {
        this.currentPage = 2;
        this.$nextTick(() => {
          this.onComplaintCreated(this.context.protocol);
        });
      });
    },

    onComplaintCreated() {
      this.$bvToast.toast(this.$i18n.t("complaint.success"), {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.loading = false;
      this.complaintCreated = true;
    },
    async nextPage() {
      const next = () => {
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = 0;
        }
      };

      if (this.currentPage === 0) {
        let valid = await this.$validator.validateAll();
        const ignoreFields = ["termsAccepted"];
        if (!this.complaint.anonymous) {
          ignoreFields.push("dataConsentAccepted");
        }
        const errors = this.errors.items.filter(
          (item) => ignoreFields.indexOf(item.field) === -1
        );
        valid = valid || errors.length === 0;
        this.infoValidated = valid;
        if (valid) {
          this.$nextTick(next);
        }
      } else if (this.currentPage === 1) {
        if (!this.$refs.upload.hasInvalidFiles) {
          next();
        }
      } else if (this.currentPage === 2) {
        if (await this.$validator.validateAll()) {
          await this.submitComplaint();
        }
      } else {
        next();
      }
    },
    previousPage() {
      this.currentPage--;
      if (this.currentPage < 0) {
        this.currentPage = this.totalPages - 1;
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
